import { useState } from 'react';
import { AuthError } from '../../../hooks/useAuth';
import { config } from '../../../config';

interface AudioGenerationResponse {
  audioUrl: string;
  message?: string;
}

/**
 * Custom hook for handling AI audio generation
 *
 * Features:
 * - Text-to-speech conversion
 * - File content processing
 * - Generation state management
 * - Error handling
 */
export const useAudioGeneration = () => {
  const [audioSrc, setAudioSrc] = useState<string | null>(null);

  /**
   * Helper function to make audio generation API calls
   * @param endpoint - API endpoint
   * @param payload - Request payload
   * @returns Promise with the API response
   */
  const audioFetch = async (endpoint: string, payload: any): Promise<AudioGenerationResponse> => {
    console.log('🎙️ Making audio generation request:', {
      url: `${config.API_URL}${endpoint}`,
      payload
    });

    const response = await fetch(`${config.API_URL}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      throw new AuthError('Please login to generate podcasts');
    } else if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Audio generation failed: ${errorText}`);
      throw new Error(errorText || 'Audio generation failed');
    } else {
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      console.log('✅ Audio generation successful');
      return { audioUrl };
    }
  };

  /**
   * Generates audio from text input and optional file content
   * @param textInput - Main text to convert to audio
   * @param fileContent - Optional additional content from file
   * @param fileType - Optional file type
   * @param deepResearch - Whether to use deep research mode
   */
  const generateAudio = async (textInput: string, fileContent: string = '', fileType: string = '', deepResearch: boolean = false): Promise<void> => {
    console.log('🎬 Starting audio generation process');

    try {
      // Validate input
      if (!textInput.trim() && !fileContent.trim()) {
        throw new Error('Please provide text input or upload a file');
      }

      console.log(`📝 Processing text input (${textInput.length} chars)`);
      if (fileContent) {
        console.log(`📄 Processing file content (${fileContent.length} chars) of type ${fileType}`);
      }
      console.log(`🔍 Deep research mode: ${deepResearch ? 'enabled' : 'disabled'}`);

      const data = await audioFetch('/api/generate_podcast', {
        text: textInput,
        fileContent,
        fileType,
        deepResearch
      });

      console.log('🎵 Setting audio source:', data.audioUrl);
      setAudioSrc(data.audioUrl);
    } catch (err) {
      setAudioSrc(null);
      throw err;
    }
  };

  /**
   * Clears the current audio and resets the state
   */
  const clearAudio = () => {
    console.log('🧹 Clearing audio state');
    setAudioSrc(null);
  };

  return {
    audioSrc,
    generateAudio,
    clearAudio,
  };
};
